/* ────────────────────────  ONE LOCALE FOR DATES  ────────────────────────────
 *
 * QA, 2026-08-27: "US date/time formats for an EU product — Shifts filters and
 * Reports time-of-day use MM/DD/YYYY and 12-hour."
 *
 * WHAT THIS FIXES AND WHAT IT CANNOT.
 *
 * Every `toLocaleDateString(undefined, …)` in this product resolves to the
 * BROWSER's locale, so the same screen renders `27/08/2026` for one reader and
 * `08/27/2026` for another. Those calls are the application's to decide, and
 * they route through here now: `DATE_LOCALE` is British, matching the product's
 * own copy ("organisation", "Summarise"), and 24-hour time.
 *
 * IT DOES NOT AND CANNOT CHANGE `<input type="date">` OR `<input type="time">`.
 * The HTML spec leaves the presentation of those controls to the user agent,
 * driven by the browser's language settings — no attribute, property or
 * stylesheet reaches it. The Shifts filters (dashboard-shifts.tsx) and the
 * Reports time-of-day fields (chrome/filter-rail.tsx) are native inputs and will
 * keep showing `MM/DD/YYYY` and a 12-hour `--:--` to a reader whose browser is
 * en-US. Replacing them with custom pickers would fix the display and cost the
 * native mobile picker, the keyboard behaviour and the accessibility that come
 * free with the real control — so they stay, and this limit is written down
 * rather than quietly accepted.
 *
 * Why a constant rather than each call site passing 'en-GB': the failure mode is
 * a NEW call site written with `undefined`, which looks right, reviews fine, and
 * renders American for half the users. One import to reach for, and one place to
 * change if the product is ever localised for real.
 */

/** The product's display locale. British, to match the product's own copy. */
export const DATE_LOCALE = 'en-GB';

/**
 * 24-hour time. `hour12: false` is passed explicitly rather than relying on the
 * locale's default, because a caller that also passes its own options object
 * would otherwise have to remember to repeat it.
 */
export const TIME_OPTIONS: Intl.DateTimeFormatOptions = {
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
};

/* THE THREE BELOW FORMAT ON THE DEVICE'S CLOCK unless `options.timeZone`
 * says otherwise. That is right for what belongs to no building — billing,
 * sessions, the account pages, the Super Admin console (R-T6) — and wrong for
 * anything a building owns: those go through function-utils/building-time with
 * the zone the response carries. This file and that one are the only two that
 * may build a date formatter (building-time.guard.test).
 *
 * A value that is not a date prints as '' rather than "Invalid Date". */
const formatWith = (
  value: Date | string | number,
  options: Intl.DateTimeFormatOptions
): string => {
  const at = new Date(value);
  if (Number.isNaN(at.getTime())) return '';
  try {
    return new Intl.DateTimeFormat(DATE_LOCALE, options).format(at);
  } catch {
    // An unusable `timeZone` in the options: the device's clock.
    const rest: Intl.DateTimeFormatOptions = { ...options };
    delete rest.timeZone;

    return new Intl.DateTimeFormat(DATE_LOCALE, rest).format(at);
  }
};

const DATE_ONLY: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
};

const hasDateField = (options: Intl.DateTimeFormatOptions): boolean =>
  Boolean(
    options.weekday ||
      options.year ||
      options.month ||
      options.day ||
      options.dateStyle
  );

/** A date under the product locale. */
export const formatDate = (
  value: Date | string | number,
  options: Intl.DateTimeFormatOptions = {}
): string =>
  formatWith(value, hasDateField(options) ? options : { ...DATE_ONLY, ...options });

/** A time under the product locale, 24-hour unless overridden. */
export const formatTime = (
  value: Date | string | number,
  options: Intl.DateTimeFormatOptions = {}
): string => formatWith(value, { ...TIME_OPTIONS, ...options });

/** Date and time together, e.g. "27/08/2026, 14:05". */
export const formatDateTime = (
  value: Date | string | number,
  options: Intl.DateTimeFormatOptions = {}
): string =>
  formatWith(value, {
    ...TIME_OPTIONS,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    ...options,
  });
